/**
 * Services Service
 *
 * API client for customer services with fallback to mock data.
 * SERVICE_ACTIVATION_PIPELINE - TIER 7 (Portal Integration)
 */

import { getCommercialRAAddress, GFL_RA_REPRESENTATIVE } from '@geauxos/core/ra/public-address';

import { getIdToken } from '../lib/auth';

// =============================================================================
// Configuration
// =============================================================================

const SERVICES_API_URL = import.meta.env.VITE_SERVICES_API_URL ?? import.meta.env.VITE_API_URL ?? '';

// =============================================================================
// Types
// =============================================================================

export type ServiceStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'CANCELLED';
export type ServiceState = string; // 2-letter state code

export interface ServiceSummary {
  serviceId: string;
  status: ServiceStatus;
  serviceType: string;
  state: ServiceState;
  entityName: string;
  effectiveDate: string;
  renewalDate: string;
  offeringName?: string;
}

export interface ServiceDetail extends ServiceSummary {
  customerId: string;
  orderId?: string;
  serviceOfferingId?: string;
  offeringName?: string;
  entityDetails: {
    entityName: string;
    entityType: string;
    sosFileNumber?: string;
    principalAddress: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
    };
  };
  raAddress: {
    representative: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ServicesListResponse {
  services: ServiceSummary[];
  nextToken?: string;
  count?: number;
}

// =============================================================================
// API Helper
// =============================================================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    nextToken?: string;
    count?: number;
  };
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = await getIdToken();

  const response = await fetch(`${SERVICES_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      'x-tenant-id': 'geauxfile',
      ...options.headers,
    },
  });

  const data = await response.json() as ApiResponse<T>;

  if (!response.ok) {
    throw new Error(data.error?.message ?? `API error: ${response.status}`);
  }

  return data;
}

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Get all services for the current user
 */
export async function getMyServices(options?: {
  limit?: number;
  nextToken?: string;
  status?: ServiceStatus;
}): Promise<ServicesListResponse> {
  if (!SERVICES_API_URL) throw new Error('Services unavailable: API not configured');

  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.nextToken) params.set('nextToken', options.nextToken);
  if (options?.status) params.set('status', options.status);

  // TODO: Pagination response shape mismatch — backend returns { data, pagination: { nextCursor } }
  // but this client reads { data, meta: { nextToken } }. Pagination won't work until aligned.
  // Not a blocker for 4 customers (all fit in single page). Fix when customer count > 20.
  const response = await apiRequest<ServiceSummary[]>(`/services?${params}`);
  const services = response.data ?? [];

  // Client-side deduplication as defense-in-depth
  const seenServiceIds = new Set<string>();
  const dedupedServices = services.filter((service) => {
    if (seenServiceIds.has(service.serviceId)) {
      return false;
    }
    seenServiceIds.add(service.serviceId);
    return true;
  });

  return {
    services: dedupedServices,
    nextToken: response.meta?.nextToken,
    count: response.meta?.count ?? dedupedServices.length,
  };
}

/**
 * Get a single service by ID
 */
export async function getServiceById(serviceId: string): Promise<ServiceDetail | null> {
  if (!SERVICES_API_URL) throw new Error('Services unavailable: API not configured');

  try {
    const response = await apiRequest<ServiceDetail>(`/services/${serviceId}`);
    return response.data ?? null;
  } catch (error) {
    console.error('Failed to fetch service:', error);
    return null;
  }
}

/**
 * Get service count for dashboard stats
 */
export async function getServiceCount(): Promise<number> {
  if (!SERVICES_API_URL) throw new Error('Services unavailable: API not configured');

  const response = await getMyServices();
  return response.count ?? response.services.length;
}

/**
 * Get services by status
 */
export async function getServicesByStatus(status: ServiceStatus): Promise<ServiceSummary[]> {
  if (!SERVICES_API_URL) throw new Error('Services unavailable: API not configured');

  const response = await getMyServices({ status });
  return response.services;
}

/**
 * Get active services count for dashboard
 */
export async function getActiveServicesCount(): Promise<number> {
  const services = await getServicesByStatus('ACTIVE');
  return services.length;
}

/**
 * Get services with upcoming renewals (within N days)
 */
export async function getServicesNearingRenewal(withinDays = 30): Promise<ServiceSummary[]> {
  const response = await getMyServices();
  const now = new Date();
  const cutoffDate = new Date(now.getTime() + withinDays * 24 * 60 * 60 * 1000);

  return response.services.filter((service) => {
    const renewalDate = new Date(service.renewalDate);
    return renewalDate <= cutoffDate && service.status === 'ACTIVE';
  });
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get display-friendly state name
 */
export function getStateName(state: ServiceState): string {
  const stateNames: Record<string, string> = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'District of Columbia',
  };
  return stateNames[state] ?? state;
}

/**
 * Get display-friendly service type name
 */
export function getServiceTypeName(serviceType: string): string {
  const typeNames: Record<string, string> = {
    registered_agent: 'Registered Agent',
    formation: 'Business Formation',
    annual_report: 'Annual Report Filing',
  };
  return typeNames[serviceType] ?? serviceType;
}

/**
 * Get status display configuration
 */
export function getStatusConfig(status: ServiceStatus): {
  label: string;
  bgColor: string;
  textColor: string;
} {
  const configs: Record<ServiceStatus, { label: string; bgColor: string; textColor: string }> = {
    PENDING: {
      label: 'Pending',
      bgColor: 'hsl(var(--color-warning-100))',
      textColor: 'hsl(var(--color-warning-700))',
    },
    ACTIVE: {
      label: 'Active',
      bgColor: 'hsl(var(--color-success-100))',
      textColor: 'hsl(var(--color-success-700))',
    },
    SUSPENDED: {
      label: 'Suspended',
      bgColor: 'hsl(var(--color-error-100))',
      textColor: 'hsl(var(--color-error-700))',
    },
    EXPIRED: {
      label: 'Expired',
      bgColor: 'hsl(var(--color-neutral-100))',
      textColor: 'hsl(var(--color-neutral-700))',
    },
    CANCELLED: {
      label: 'Cancelled',
      bgColor: 'hsl(var(--color-neutral-100))',
      textColor: 'hsl(var(--color-neutral-700))',
    },
  };
  return configs[status];
}

/**
 * Get the customer-facing registered agent address for a state.
 * Only official commercial addresses are returned (EAI-136); states without
 * one return undefined and callers show their "address pending" copy.
 */
export function getRAAddressForState(state: ServiceState): {
  representative: string;
  street1: string;
  city: string;
  stateCode: string;
  zip: string;
} | undefined {
  const address = getCommercialRAAddress(state);
  return address ? { representative: GFL_RA_REPRESENTATIVE, ...address } : undefined;
}
